/*
 * QR Code generator library (TypeScript)
 *
 * Copyright (c) Project Nayuki. (MIT License)
 * https://www.nayuki.io/page/qr-code-generator-library
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of
 * this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to
 * use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
 * the Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 * - The above copyright notice and this permission notice shall be included in
 *   all copies or substantial portions of the Software.
 * - The Software is provided "as is", without warranty of any kind, express or
 *   implied, including but not limited to the warranties of merchantability,
 *   fitness for a particular purpose and noninfringement. In no event shall the
 *   authors or copyright holders be liable for any claim, damages or other
 *   liability, whether in an action of contract, tort or otherwise, arising from,
 *   out of or in connection with the Software or the use or other dealings in the
 *   Software.
 */

/*
 * A port of Project Nayuki's QR Code generator to this repository's lint rules: plain
 * functions and data instead of classes and namespaces, and no reassigned parameters.
 * The algorithm is Nayuki's, unchanged — the codeword tables, the Reed–Solomon
 * arithmetic over GF(2⁸) with the polynomial 0x11D, the function patterns, the zigzag
 * placement and the four mask penalty rules of ISO/IEC 18004. Kanji and ECI segments
 * are left out: this port encodes numeric, alphanumeric and byte (UTF-8) data.
 *
 * One addition: when the mask is chosen automatically, the penalty of all eight masks
 * is returned with the code, so a page can show why the chosen one won.
 */

// biome-ignore-start lint/suspicious/noBitwiseOperators: QR encoding is bit packing, BCH codes and GF(2^8) arithmetic

export type QrEcc = "L" | "M" | "Q" | "H";

/** Low to high: about 7%, 15%, 25% and 30% of codewords can be restored. */
export const QR_ECC_LEVELS: readonly QrEcc[] = ["L", "M", "Q", "H"];

const ECC_ORDINAL: Readonly<Record<QrEcc, number>> = { L: 0, M: 1, Q: 2, H: 3 };

/** The two bits each level writes into the format information. */
const ECC_FORMAT_BITS: Readonly<Record<QrEcc, number>> = {
  L: 1,
  M: 0,
  Q: 3,
  H: 2,
};

export type QrMode = "numeric" | "alphanumeric" | "byte";

const MODE_BITS: Readonly<Record<QrMode, number>> = {
  numeric: 0x1,
  alphanumeric: 0x2,
  byte: 0x4,
};

/** Width of the character-count field for versions 1–9, 10–26 and 27–40. */
const CHAR_COUNT_BITS: Readonly<
  Record<QrMode, readonly [number, number, number]>
> = {
  numeric: [10, 12, 14],
  alphanumeric: [9, 11, 13],
  byte: [8, 16, 16],
};

export const MIN_VERSION = 1;
export const MAX_VERSION = 40;

const PENALTY_N1 = 3;
const PENALTY_N2 = 3;
const PENALTY_N3 = 40;
const PENALTY_N4 = 10;

/** Error-correction codewords per block, by level then version (index 0 unused). */
const ECC_CODEWORDS_PER_BLOCK: readonly (readonly number[])[] = [
  [
    -1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30,
    28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30,
    30, 30, 30,
  ],
  [
    -1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26,
    26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28,
    28, 28, 28,
  ],
  [
    -1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28,
    26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30,
    30, 30, 30,
  ],
  [
    -1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28,
    26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30,
    30, 30, 30,
  ],
];

/** Error-correction blocks, by level then version (index 0 unused). */
const NUM_ERROR_CORRECTION_BLOCKS: readonly (readonly number[])[] = [
  [
    -1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10,
    12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25,
  ],
  [
    -1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17,
    17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49,
  ],
  [
    -1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23,
    23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68,
  ],
  [
    -1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25,
    25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77,
    81,
  ],
];

const NUMERIC = /^[0-9]*$/;
const ALPHANUMERIC = /^[A-Z0-9 $%*+./:-]*$/;
const ALPHANUMERIC_CHARSET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ $%*+-./:";

function appendBits(value: number, length: number, bits: number[]): void {
  if (length < 0 || length > 31 || value >>> length !== 0) {
    throw new RangeError("Value out of range");
  }
  for (let i = length - 1; i >= 0; i -= 1) {
    bits.push((value >>> i) & 1);
  }
}

function getBit(value: number, index: number): boolean {
  return ((value >>> index) & 1) !== 0;
}

/* ------------------------------------------------------------------ segments */

export interface QrSegment {
  readonly mode: QrMode;
  /** Characters for numeric and alphanumeric data; bytes for byte data. */
  readonly numChars: number;
  readonly bits: readonly number[];
}

export function charCountBits(mode: QrMode, version: number): number {
  return CHAR_COUNT_BITS[mode][Math.floor((version + 7) / 17)];
}

export function isNumeric(text: string): boolean {
  return NUMERIC.test(text);
}

/** Digits, capital letters, space and $ % * + - . / : — the 45-character set. */
export function isAlphanumeric(text: string): boolean {
  return ALPHANUMERIC.test(text);
}

/** UTF-8, with a lone surrogate written as U+FFFD (what `TextEncoder` does). */
export function utf8Bytes(text: string): number[] {
  return [...new TextEncoder().encode(text)];
}

export function makeBytes(data: readonly number[]): QrSegment {
  const bits: number[] = [];
  for (const byte of data) {
    appendBits(byte, 8, bits);
  }
  return { mode: "byte", numChars: data.length, bits };
}

export function makeNumeric(digits: string): QrSegment {
  if (!isNumeric(digits)) {
    throw new RangeError("String contains non-numeric characters");
  }
  const bits: number[] = [];
  for (let i = 0; i < digits.length; ) {
    const n = Math.min(digits.length - i, 3);
    appendBits(Number.parseInt(digits.slice(i, i + n), 10), n * 3 + 1, bits);
    i += n;
  }
  return { mode: "numeric", numChars: digits.length, bits };
}

export function makeAlphanumeric(text: string): QrSegment {
  if (!isAlphanumeric(text)) {
    throw new RangeError(
      "String contains unencodable characters in alphanumeric mode"
    );
  }
  const bits: number[] = [];
  let i = 0;
  for (; i + 2 <= text.length; i += 2) {
    const pair =
      ALPHANUMERIC_CHARSET.indexOf(text.charAt(i)) * 45 +
      ALPHANUMERIC_CHARSET.indexOf(text.charAt(i + 1));
    appendBits(pair, 11, bits);
  }
  if (i < text.length) {
    appendBits(ALPHANUMERIC_CHARSET.indexOf(text.charAt(i)), 6, bits);
  }
  return { mode: "alphanumeric", numChars: text.length, bits };
}

/** The most compact single mode the whole text fits: numeric, then alphanumeric, then bytes. */
export function makeSegments(text: string): QrSegment[] {
  if (text === "") {
    return [];
  }
  if (isNumeric(text)) {
    return [makeNumeric(text)];
  }
  if (isAlphanumeric(text)) {
    return [makeAlphanumeric(text)];
  }
  return [makeBytes(utf8Bytes(text))];
}

/** Bits the segments need at a version, or Infinity when a count field overflows. */
export function totalBits(
  segments: readonly QrSegment[],
  version: number
): number {
  let result = 0;
  for (const segment of segments) {
    const countBits = charCountBits(segment.mode, version);
    if (segment.numChars >= 1 << countBits) {
      return Number.POSITIVE_INFINITY;
    }
    result += 4 + countBits + segment.bits.length;
  }
  return result;
}

/* ------------------------------------------------------------------ capacity */

/** Modules available for data and error correction (remainder bits included). */
export function numRawDataModules(version: number): number {
  if (version < MIN_VERSION || version > MAX_VERSION) {
    throw new RangeError("Version number out of range");
  }
  let result = (16 * version + 128) * version + 64;
  if (version >= 2) {
    const numAlign = Math.floor(version / 7) + 2;
    result -= (25 * numAlign - 10) * numAlign - 55;
    if (version >= 7) {
      result -= 36;
    }
  }
  return result;
}

/** Data codewords (8-bit) a version holds at an error-correction level. */
export function numDataCodewords(version: number, ecc: QrEcc): number {
  const level = ECC_ORDINAL[ecc];
  return (
    Math.floor(numRawDataModules(version) / 8) -
    ECC_CODEWORDS_PER_BLOCK[level][version] *
      NUM_ERROR_CORRECTION_BLOCKS[level][version]
  );
}

/** Error-correction blocks and codewords per block, for display. */
export function blockStructure(
  version: number,
  ecc: QrEcc
): { blocks: number; eccPerBlock: number } {
  const level = ECC_ORDINAL[ecc];
  return {
    blocks: NUM_ERROR_CORRECTION_BLOCKS[level][version],
    eccPerBlock: ECC_CODEWORDS_PER_BLOCK[level][version],
  };
}

/* ------------------------------------------------------------- Reed–Solomon */

/** Product of two field elements in GF(2⁸/0x11D). */
export function rsMultiply(x: number, y: number): number {
  if (x >>> 8 !== 0 || y >>> 8 !== 0) {
    throw new RangeError("Byte out of range");
  }
  let z = 0;
  for (let i = 7; i >= 0; i -= 1) {
    z = (z << 1) ^ ((z >>> 7) * 0x1_1d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z;
}

/**
 * The generator polynomial (x − r⁰)(x − r¹)…(x − r^{degree−1}) with r = 0x02, highest
 * power first and the leading 1 left out.
 */
export function rsDivisor(degree: number): number[] {
  if (degree < 1 || degree > 255) {
    throw new RangeError("Degree out of range");
  }
  const result: number[] = Array.from({ length: degree }, () => 0);
  result[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i += 1) {
    for (let j = 0; j < result.length; j += 1) {
      result[j] = rsMultiply(result[j], root);
      if (j + 1 < result.length) {
        result[j] ^= result[j + 1];
      }
    }
    root = rsMultiply(root, 0x02);
  }
  return result;
}

/** The error-correction codewords for `data`: the remainder of the division. */
export function rsRemainder(
  data: readonly number[],
  divisor: readonly number[]
): number[] {
  const result: number[] = divisor.map(() => 0);
  for (const byte of data) {
    const factor = byte ^ (result.shift() ?? 0);
    result.push(0);
    for (const [i, coefficient] of divisor.entries()) {
      result[i] ^= rsMultiply(coefficient, factor);
    }
  }
  return result;
}

/* ----------------------------------------------------------------- codewords */

/**
 * Mode, count and data of every segment, the terminator, zero bits to a byte boundary
 * and the alternating pad bytes 0xEC 0x11 — packed into the version's data codewords.
 */
export function dataCodewords(
  segments: readonly QrSegment[],
  version: number,
  ecc: QrEcc
): number[] {
  const bits: number[] = [];
  for (const segment of segments) {
    appendBits(MODE_BITS[segment.mode], 4, bits);
    appendBits(segment.numChars, charCountBits(segment.mode, version), bits);
    for (const bit of segment.bits) {
      bits.push(bit);
    }
  }
  const capacity = numDataCodewords(version, ecc) * 8;
  if (bits.length > capacity) {
    throw new RangeError("Data too long");
  }
  appendBits(0, Math.min(4, capacity - bits.length), bits);
  appendBits(0, (8 - (bits.length % 8)) % 8, bits);
  for (let pad = 0xec; bits.length < capacity; pad ^= 0xec ^ 0x11) {
    appendBits(pad, 8, bits);
  }
  const codewords: number[] = Array.from({ length: bits.length / 8 }, () => 0);
  for (const [i, bit] of bits.entries()) {
    codewords[i >>> 3] |= bit << (7 - (i & 7));
  }
  return codewords;
}

/**
 * Split the data into blocks, append each block's error correction, and interleave:
 * the first codeword of every block, then the second, and so on — the short blocks
 * come first and have one data codeword fewer.
 */
export function addEccAndInterleave(
  data: readonly number[],
  version: number,
  ecc: QrEcc
): number[] {
  if (data.length !== numDataCodewords(version, ecc)) {
    throw new RangeError("Invalid argument");
  }
  const { blocks: numBlocks, eccPerBlock } = blockStructure(version, ecc);
  const rawCodewords = Math.floor(numRawDataModules(version) / 8);
  const numShortBlocks = numBlocks - (rawCodewords % numBlocks);
  const shortBlockLength = Math.floor(rawCodewords / numBlocks);
  const divisor = rsDivisor(eccPerBlock);
  const blocks: number[][] = [];
  let offset = 0;
  for (let i = 0; i < numBlocks; i += 1) {
    const length =
      shortBlockLength - eccPerBlock + (i < numShortBlocks ? 0 : 1);
    const block = data.slice(offset, offset + length);
    offset += length;
    const remainder = rsRemainder(block, divisor);
    if (i < numShortBlocks) {
      block.push(0);
    }
    blocks.push([...block, ...remainder]);
  }
  const result: number[] = [];
  for (let i = 0; i < blocks[0].length; i += 1) {
    for (const [j, block] of blocks.entries()) {
      // Skip the placeholder that evens up the short blocks.
      if (i !== shortBlockLength - eccPerBlock || j >= numShortBlocks) {
        result.push(block[i]);
      }
    }
  }
  return result;
}

/* -------------------------------------------------------------------- matrix */

interface Grid {
  readonly size: number;
  readonly modules: boolean[][];
  readonly isFunction: boolean[][];
}

function emptyGrid(size: number): Grid {
  const blank = () =>
    Array.from({ length: size }, () =>
      Array.from({ length: size }, () => false)
    );
  return { size, modules: blank(), isFunction: blank() };
}

function setFunctionModule(grid: Grid, x: number, y: number, dark: boolean) {
  grid.modules[y][x] = dark;
  grid.isFunction[y][x] = true;
}

/** The 15 format bits (level and mask, BCH-protected, XORed with 0x5412). */
export function formatBits(ecc: QrEcc, mask: number): number {
  const data = (ECC_FORMAT_BITS[ecc] << 3) | mask;
  let remainder = data;
  for (let i = 0; i < 10; i += 1) {
    remainder = (remainder << 1) ^ ((remainder >>> 9) * 0x5_37);
  }
  return ((data << 10) | remainder) ^ 0x54_12;
}

/** The 18 version bits (BCH-protected), drawn from version 7 up. */
export function versionBits(version: number): number {
  let remainder = version;
  for (let i = 0; i < 12; i += 1) {
    remainder = (remainder << 1) ^ ((remainder >>> 11) * 0x1f_25);
  }
  return (version << 12) | remainder;
}

/** Centre coordinates of the alignment patterns, ascending. */
export function alignmentPatternPositions(version: number): number[] {
  if (version === 1) {
    return [];
  }
  const numAlign = Math.floor(version / 7) + 2;
  const step =
    Math.floor((version * 8 + numAlign * 3 + 5) / (numAlign * 4 - 4)) * 2;
  const result = [6];
  for (
    let position = version * 4 + 17 - 7;
    result.length < numAlign;
    position -= step
  ) {
    result.splice(1, 0, position);
  }
  return result;
}

function drawFormatBits(grid: Grid, ecc: QrEcc, mask: number): void {
  const bits = formatBits(ecc, mask);
  const { size } = grid;
  // First copy, around the top-left finder.
  for (let i = 0; i <= 5; i += 1) {
    setFunctionModule(grid, 8, i, getBit(bits, i));
  }
  setFunctionModule(grid, 8, 7, getBit(bits, 6));
  setFunctionModule(grid, 8, 8, getBit(bits, 7));
  setFunctionModule(grid, 7, 8, getBit(bits, 8));
  for (let i = 9; i < 15; i += 1) {
    setFunctionModule(grid, 14 - i, 8, getBit(bits, i));
  }
  // Second copy, split between the other two finders.
  for (let i = 0; i < 8; i += 1) {
    setFunctionModule(grid, size - 1 - i, 8, getBit(bits, i));
  }
  for (let i = 8; i < 15; i += 1) {
    setFunctionModule(grid, 8, size - 15 + i, getBit(bits, i));
  }
  setFunctionModule(grid, 8, size - 8, true);
}

function drawVersion(grid: Grid, version: number): void {
  if (version < 7) {
    return;
  }
  const bits = versionBits(version);
  for (let i = 0; i < 18; i += 1) {
    const dark = getBit(bits, i);
    const a = grid.size - 11 + (i % 3);
    const b = Math.floor(i / 3);
    setFunctionModule(grid, a, b, dark);
    setFunctionModule(grid, b, a, dark);
  }
}

function drawFinderPattern(grid: Grid, x: number, y: number): void {
  for (let dy = -4; dy <= 4; dy += 1) {
    for (let dx = -4; dx <= 4; dx += 1) {
      const distance = Math.max(Math.abs(dx), Math.abs(dy));
      const xx = x + dx;
      const yy = y + dy;
      if (xx >= 0 && xx < grid.size && yy >= 0 && yy < grid.size) {
        setFunctionModule(grid, xx, yy, distance !== 2 && distance !== 4);
      }
    }
  }
}

function drawAlignmentPattern(grid: Grid, x: number, y: number): void {
  for (let dy = -2; dy <= 2; dy += 1) {
    for (let dx = -2; dx <= 2; dx += 1) {
      setFunctionModule(
        grid,
        x + dx,
        y + dy,
        Math.max(Math.abs(dx), Math.abs(dy)) !== 1
      );
    }
  }
}

function drawFunctionPatterns(grid: Grid, version: number, ecc: QrEcc): void {
  const { size } = grid;
  for (let i = 0; i < size; i += 1) {
    setFunctionModule(grid, 6, i, i % 2 === 0);
    setFunctionModule(grid, i, 6, i % 2 === 0);
  }
  drawFinderPattern(grid, 3, 3);
  drawFinderPattern(grid, size - 4, 3);
  drawFinderPattern(grid, 3, size - 4);
  const positions = alignmentPatternPositions(version);
  const last = positions.length - 1;
  for (const [i, row] of positions.entries()) {
    for (const [j, column] of positions.entries()) {
      // Not on the three finder corners.
      const corner =
        (i === 0 && j === 0) ||
        (i === 0 && j === last) ||
        (i === last && j === 0);
      if (!corner) {
        drawAlignmentPattern(grid, row, column);
      }
    }
  }
  drawFormatBits(grid, ecc, 0);
  drawVersion(grid, version);
}

/** Place the codeword bits in the zigzag, two columns at a time, skipping column 6. */
function drawCodewords(grid: Grid, codewords: readonly number[]): void {
  const { size } = grid;
  const totalBitCount = codewords.length * 8;
  let i = 0;
  for (let right = size - 1; right >= 1; right -= 2) {
    const column = right === 6 ? 5 : right;
    const upward = ((column + 1) & 2) === 0;
    for (let vert = 0; vert < size; vert += 1) {
      const y = upward ? size - 1 - vert : vert;
      for (let j = 0; j < 2; j += 1) {
        const x = column - j;
        if (!grid.isFunction[y][x] && i < totalBitCount) {
          grid.modules[y][x] = getBit(codewords[i >>> 3], 7 - (i & 7));
          i += 1;
        }
      }
    }
    right = column;
  }
}

/** Does mask pattern `mask` invert the module at column x, row y? */
export function maskInverts(mask: number, x: number, y: number): boolean {
  switch (mask) {
    case 0:
      return (x + y) % 2 === 0;
    case 1:
      return y % 2 === 0;
    case 2:
      return x % 3 === 0;
    case 3:
      return (x + y) % 3 === 0;
    case 4:
      return (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0;
    case 5:
      return ((x * y) % 2) + ((x * y) % 3) === 0;
    case 6:
      return (((x * y) % 2) + ((x * y) % 3)) % 2 === 0;
    case 7:
      return (((x + y) % 2) + ((x * y) % 3)) % 2 === 0;
    default:
      throw new RangeError("Mask value out of range");
  }
}

/** XOR the mask over the data modules; applying it twice undoes it. */
function applyMask(grid: Grid, mask: number): void {
  for (let y = 0; y < grid.size; y += 1) {
    for (let x = 0; x < grid.size; x += 1) {
      if (!grid.isFunction[y][x] && maskInverts(mask, x, y)) {
        grid.modules[y][x] = !grid.modules[y][x];
      }
    }
  }
}

/* ------------------------------------------------------------------ penalty */

function addRunToHistory(
  runLength: number,
  history: number[],
  size: number
): void {
  // The light border before the first run counts as part of it.
  const length = history[0] === 0 ? runLength + size : runLength;
  history.pop();
  history.unshift(length);
}

/** 1:1:3:1:1 finder-like patterns with four light modules on one side or the other. */
function countFinderPatterns(history: readonly number[]): number {
  const n = history[1];
  const core =
    n > 0 &&
    history[2] === n &&
    history[3] === n * 3 &&
    history[4] === n &&
    history[5] === n;
  const before = core && history[0] >= n * 4 && history[6] >= n ? 1 : 0;
  const after = core && history[6] >= n * 4 && history[0] >= n ? 1 : 0;
  return before + after;
}

function terminateRuns(
  runColor: boolean,
  runLength: number,
  history: number[],
  size: number
): number {
  let current = runLength;
  if (runColor) {
    addRunToHistory(current, history, size);
    current = 0;
  }
  // The light border after the last run.
  addRunToHistory(current + size, history, size);
  return countFinderPatterns(history);
}

/** Rules 1 and 3 along one row or column: long runs, and finder-like patterns. */
function linePenalty(line: readonly boolean[]): number {
  const size = line.length;
  const history = [0, 0, 0, 0, 0, 0, 0];
  let result = 0;
  let runColor = false;
  let runLength = 0;
  for (const dark of line) {
    if (dark === runColor) {
      runLength += 1;
      if (runLength === 5) {
        result += PENALTY_N1;
      } else if (runLength > 5) {
        result += 1;
      }
    } else {
      addRunToHistory(runLength, history, size);
      if (!runColor) {
        result += countFinderPatterns(history) * PENALTY_N3;
      }
      runColor = dark;
      runLength = 1;
    }
  }
  return (
    result + terminateRuns(runColor, runLength, history, size) * PENALTY_N3
  );
}

/** Rule 2: every 2 × 2 block of one colour. */
function blockPenalty(modules: readonly (readonly boolean[])[]): number {
  let result = 0;
  for (let y = 0; y < modules.length - 1; y += 1) {
    for (let x = 0; x < modules.length - 1; x += 1) {
      const dark = modules[y][x];
      if (
        dark === modules[y][x + 1] &&
        dark === modules[y + 1][x] &&
        dark === modules[y + 1][x + 1]
      ) {
        result += PENALTY_N2;
      }
    }
  }
  return result;
}

/** Rule 4: 10 points for every 5% the dark share strays beyond 45–55%. */
function balancePenalty(modules: readonly (readonly boolean[])[]): number {
  let dark = 0;
  for (const row of modules) {
    for (const module of row) {
      dark += module ? 1 : 0;
    }
  }
  const total = modules.length * modules.length;
  const k = Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1;
  return k * PENALTY_N4;
}

/** The ISO/IEC 18004 penalty score of a finished symbol: lower is easier to scan. */
export function penaltyScore(modules: readonly (readonly boolean[])[]): number {
  let result = 0;
  for (const row of modules) {
    result += linePenalty(row);
  }
  for (let x = 0; x < modules.length; x += 1) {
    result += linePenalty(modules.map((row) => row[x]));
  }
  return result + blockPenalty(modules) + balancePenalty(modules);
}

/* -------------------------------------------------------------------- encode */

export interface QrCode {
  version: number;
  /** Modules per side: 4 × version + 17. */
  size: number;
  ecc: QrEcc;
  mask: number;
  /** `modules[y][x]`, true for dark. */
  modules: boolean[][];
  /** The penalty of each mask 0–7 when the mask was chosen automatically. */
  penalties: number[] | null;
}

export interface QrOptions {
  minVersion?: number;
  maxVersion?: number;
  /** 0–7, or −1 to choose the mask with the lowest penalty. */
  mask?: number;
  /** Raise the level while the data still fits the same version. */
  boostEcc?: boolean;
}

/** The smallest version in range the segments fit at `ecc`, or null. */
export function fitVersion(
  segments: readonly QrSegment[],
  ecc: QrEcc,
  minVersion = MIN_VERSION,
  maxVersion = MAX_VERSION
): number | null {
  for (let version = minVersion; version <= maxVersion; version += 1) {
    if (totalBits(segments, version) <= numDataCodewords(version, ecc) * 8) {
      return version;
    }
  }
  return null;
}

function chooseMask(
  grid: Grid,
  ecc: QrEcc
): { mask: number; penalties: number[] } {
  const penalties: number[] = [];
  for (let mask = 0; mask < 8; mask += 1) {
    applyMask(grid, mask);
    drawFormatBits(grid, ecc, mask);
    penalties.push(penaltyScore(grid.modules));
    applyMask(grid, mask);
  }
  const best = Math.min(...penalties);
  return { mask: penalties.indexOf(best), penalties };
}

/** Encode segments, or return null when they do not fit any version in range. */
export function encodeSegments(
  segments: readonly QrSegment[],
  requestedEcc: QrEcc,
  options: QrOptions = {}
): QrCode | null {
  const {
    minVersion = MIN_VERSION,
    maxVersion = MAX_VERSION,
    mask = -1,
    boostEcc = true,
  } = options;
  if (
    minVersion < MIN_VERSION ||
    maxVersion > MAX_VERSION ||
    minVersion > maxVersion ||
    mask < -1 ||
    mask > 7
  ) {
    throw new RangeError("Invalid value");
  }
  const version = fitVersion(segments, requestedEcc, minVersion, maxVersion);
  if (version === null) {
    return null;
  }
  const used = totalBits(segments, version);
  let ecc = requestedEcc;
  for (const level of QR_ECC_LEVELS.slice(ECC_ORDINAL[requestedEcc] + 1)) {
    if (boostEcc && used <= numDataCodewords(version, level) * 8) {
      ecc = level;
    }
  }
  const grid = emptyGrid(version * 4 + 17);
  drawFunctionPatterns(grid, version, ecc);
  const codewords = addEccAndInterleave(
    dataCodewords(segments, version, ecc),
    version,
    ecc
  );
  drawCodewords(grid, codewords);
  const chosen = mask === -1 ? chooseMask(grid, ecc) : null;
  const finalMask = chosen ? chosen.mask : mask;
  applyMask(grid, finalMask);
  drawFormatBits(grid, ecc, finalMask);
  return {
    version,
    size: grid.size,
    ecc,
    mask: finalMask,
    modules: grid.modules,
    penalties: chosen ? chosen.penalties : null,
  };
}

/** Encode text in the most compact single mode, or return null when it is too long. */
export function encodeText(
  text: string,
  ecc: QrEcc,
  options: QrOptions = {}
): QrCode | null {
  return encodeSegments(makeSegments(text), ecc, options);
}

// biome-ignore-end lint/suspicious/noBitwiseOperators: end of the QR encoder
